import SimplexNoise from "simplex-noise";
import { drawPixel } from "./utils";
import { ThreadPool } from "./ThreadPool";
const { Noise } = require("noisejs");
const SEED = 0;

export type Modifiers = {
  doErosion: boolean;
  /**
   * @deprecated
   * filter is disabled for now
   */
  doFilter: boolean;
  absoluteNoise: boolean;
  invertNoise: boolean;
};

export type ModifiersType = keyof Modifiers;

export type ModifierValues = {
  seed: number;
  scale: number;
  selfSimilarity: number;
  numOctaves: number;
  filterKernelSize: number;
  erosionScale: number;
  erosionVectors: Vec2[];
  offsetX: number;
  offsetY: number;
};

export type ProcessOptionsType = keyof ProcessOptions;

export type ProcessOptions = {
  multithreading?: boolean;
  noiseGenerator?: "simplex" | "perlin";
};

export type GeneratorOptions = Modifiers & ModifierValues & ProcessOptions;

export type GetNoiseFunctionType = () => {
  noiseCtx: CanvasRenderingContext2D;
  noiseData: ImageData;
  /**
   * @description
   * READONLY
   */
  noiseCanvas: HTMLCanvasElement;
  width: number;
  height: number;
} | null;

export type Heightmap = Array<Array<number>>;

function joinHeightmaps(heightmaps: Heightmap[]) {
  const numHeightmaps = heightmaps.length;
  const numRows = Math.sqrt(numHeightmaps);
  const numCols = numRows;

  if (Math.floor(numRows) !== numRows) {
    throw new Error("The number of heightmaps must be a perfect square.");
  }

  const subWidth = heightmaps[0][0].length;
  const subHeight = heightmaps[0].length;

  const fullHeight = subHeight * numRows;
  const fullWidth = subWidth * numCols;

  const combinedHeightmap = Array.from({ length: fullHeight }, (_, y) => {
    return Array.from({ length: fullWidth }, (_, x) => {
      const rowIndex = Math.floor(y / subHeight);
      const colIndex = Math.floor(x / subWidth);
      const index = rowIndex * numCols + colIndex;
      const offsetY = rowIndex * subHeight;
      const offsetX = colIndex * subWidth;

      const value = heightmaps[index][y - offsetY][x - offsetX];
      return value;
    });
  });

  return combinedHeightmap;
}

const N = 4; // heightmap divisions

const pool = new ThreadPool(Math.pow(N, 2)); // heightmap is divided into 16 smaller squares

export async function generateNoiseV2(
  options: GeneratorOptions,
  getNoiseContextAndData: GetNoiseFunctionType
) {
  const { doFilter, filterKernelSize, multithreading } = options;

  const contextAndData = getNoiseContextAndData();
  if (!contextAndData) {
    return;
  }

  const { noiseCtx, noiseData, height, width } = contextAndData;

  let heightmap: Heightmap;
  let min: number, max: number;

  if (multithreading) {
    const tasks = [];
    for (let i = 0; i < N; i++) {
      for (let j = 0; j < N; j++) {
        tasks.push(
          pool.runTask({
            width: width / N,
            height: height / N,
            options,
            rootX: (width / N) * i,
            rootY: (height / N) * j,
          })
        );
      }
    }

    const results = await Promise.all(tasks);

    heightmap = joinHeightmaps(results.map(([heightmap]) => heightmap));
    min = Math.min(...results.map(([, min]) => min));
    max = Math.max(...results.map(([, , max]) => max));
  } else {
    [heightmap, min, max] = createHeighmap2(width, height, options, 0, 0);
  }

  
  normalize(heightmap, min, max);

  let color;

  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      color = heightmap[x][y] * 255;
      drawPixel(noiseData, x, y, width, [color, color, color]);
    }
  }

  noiseCtx.putImageData(noiseData, 0, 0);
}

function normalize(arr: Array<Array<number>>, min: number, max: number) {
  for (let i = 0; i < arr.length; i++) {
    for (let j = 0; j < arr[i].length; j++) {
      arr[i][j] = (arr[i][j] - min) / (max - min);
    }
  }


  return arr;
}

export type Vec2 = {
  x: number;
  y: number;
};

export const vec2 = (x: number, y: number) => ({ x, y });

const addVec2 = (...points: Vec2[]) =>
  points.reduce(
    (prev, curr) => ({
      x: prev.x + curr.x,
      y: prev.y + curr.y,
    }),
    { x: 0, y: 0 }
  );

const multVec2 = (p: Vec2, scalar: number) => ({
  x: p.x * scalar,
  y: p.y * scalar,
});

export function createHeighmap2(
  width: number,
  height: number,
  options: GeneratorOptions,
  rootX: number,
  rootY: number
): [Heightmap, number, number] {
  const {
    seed,
    selfSimilarity,
    numOctaves,
    scale,
    doErosion,
    erosionVectors,
    invertNoise,
    absoluteNoise,
    erosionScale,
    offsetX,
    offsetY,
    noiseGenerator,
  } = options;

  const noise = new Noise(seed);
  const noise2D =
    noiseGenerator === "perlin"
      ? (x: number, y: number) => noise.perlin2(x, y)
      : (x: number, y: number) => noise.simplex2(x, y);

  let heightmap = new Array(width).fill(0).map(() => new Array(height).fill(0));

  const H = selfSimilarity; // H parameter determines the selfimilarity of the curve

  function fbm(p: Vec2) {
    let G = Math.pow(2, -H);
    let f = 1.0; // frequency
    let a = 1.0; // amplitude
    let t = 0.0; // total value
    let v;
    for (let i = 0; i < numOctaves; i++) {
      v = noise2D(offsetX + (f * p.x) / scale, offsetY + (f * p.y) / scale);
      if (absoluteNoise) {
        v = Math.abs(v);
      }

      t += a * v;
      f *= 2.0;
      a *= G;
    }
    return t;
  }

  function pattern(p: Vec2) {
    const q = vec2(
      fbm(addVec2(p, erosionVectors[0])),
      fbm(addVec2(p, erosionVectors[1]))
    );

    const r = vec2(
      fbm(addVec2(p, multVec2(q, erosionScale), erosionVectors[2])),
      fbm(addVec2(p, multVec2(q, erosionScale), erosionVectors[3]))
    );

    return fbm(addVec2(p, multVec2(r, erosionScale)));
  }

  let mult = invertNoise ? -1 : 1;

  let min = Infinity,
    max = -Infinity;
  let val = 0;

  if (doErosion) {
    for (let x = rootX; x < width + rootX; x++) {
      for (let y = rootY; y < height + rootY; y++) {
        val = pattern(vec2(x, y)) * mult;
        if (val < min) min = val;
        if (val > max) max = val;
        heightmap[x - rootX][y - rootY] = val;
      }
    }
  } else {
    for (let x = rootX; x < width + rootX; x++) {
      for (let y = rootY; y < height + rootY; y++) {
        val = fbm(vec2(x, y)) * mult;
        if (val < min) min = val;
        if (val > max) max = val;
        heightmap[x - rootX][y - rootY] = val;
      }
    }
  }

  return [heightmap, min, max];
}

/**
 * @deprecated
 */
function createHeighmap(width: number, height: number) {
  const simplex = new SimplexNoise(SEED);
  let heightmap = new Array(width).fill(0).map(() => new Array(height).fill(0));

  const octaves = 10; // number of octaves
  const persistance = 0.4; // persistance value
  const lacunarity = 2.5; // lacunarity value
  const scale = 200;

  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      let finalHeight = 0;
      let amplitude = 1;
      let frequency = 1;

      for (let i = 0; i < octaves; i++) {
        const n = simplex.noise2D(
          (x * frequency) / scale,
          (y * frequency) / scale
        );
        finalHeight += n * amplitude;
        amplitude *= persistance;
        frequency *= lacunarity;
      }

      heightmap[x][y] = finalHeight;
    }
  }

  return heightmap;
}
